<template>
  <div class="space-y-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-xl font-bold text-slate-800">Config</h3>
    </div>
    <h3 v-if="toggleLoading" class="text-xs font-medium text-slate-500">
      Loading WhatsApp config...
    </h3>
    <div v-else>
      <section class="overflow-hidden rounded-lg">
        <div
          v-for="item in settings"
          :key="item.id"
          class="relative mb-4 flex items-center gap-4 rounded-lg border border-slate-100 bg-white px-6 py-4"
        >
          <span class="absolute left-0 top-0 h-full w-1 rounded-l bg-blue-500"></span>
          <div
            v-if="item.type !== 'toggle'"
            class="text-sm font-semibold text-slate-800"
          >
            {{ item.label }}
          </div>
          <div>
            <template v-if="item.type === 'toggle'">
              <label
                class="flex items-center gap-2 text-xs font-medium text-slate-500"
              >
                <span class="sr-only">Toggle {{ item.label }}</span>
                <span class="config-toggle cursor-pointer">
                  <input
                    type="checkbox"
                    class="config-toggle__input"
                    :checked="bannerEnabled"
                    :disabled="toggleLoading || !tenantId"
                    @change="handleToggleBanner($event.target.checked)"
                  />
                  <span class="config-toggle__track">
                    <span class="config-toggle__thumb"></span>
                  </span>
                </span>
                <span class="text-sm font-semibold text-slate-800">
                  Enable pending payment banner to this tenant
                </span>
              </label>
            </template>
            <template v-else>
              <button
                type="button"
                :class="buttonClass(item.variant)"
                :disabled="actionDisabled(item.id)"
                @click="handleAction(item.id)"
              >
                {{ actionLabel(item) }}
              </button>
            </template>
          </div>
        </div>
      </section>

      <div
        v-if="showWhatsAppSection"
        class="mb-10 space-y-4 relative flex flex-col gap-4 rounded-lg border border-slate-100 bg-white px-6 py-4"
      >
        <span class="absolute left-0 top-0 h-full w-1 rounded-l bg-blue-500"></span>
        <div class="flex items-center gap-3 mb-6">
          <div class="relative inline-block">
            <input
              id="whatsapp-toggle"
              type="checkbox"
              v-model="isWhatsAppEnabled"
              class="sr-only"
            />
            <label
              for="whatsapp-toggle"
              class="toggle-switch cursor-pointer"
              :class="{ 'toggle-switch-on': isWhatsAppEnabled }"
              aria-label="Enable WhatsApp"
            >
              <span class="toggle-slider"></span>
            </label>
          </div>
          <p class="font-medium text-gray-700">Enable WhatsApp</p>
        </div>

        <div class="relative mb-6">
          <div
            class="absolute badge-dot inline-flex items-center bg-gray-600 border border-slate-500 px-5 py-2 text-xs font-semibold text-white"
          >
            <span>Attachment</span>
          </div>

          <div class="mt-2 space-y-3 rounded-lg border border-gray-300 p-4">
            <div
              class="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-300 p-3 transition-colors hover:bg-gray-50"
              @click="triggerFileUpload"
              style="margin-top: 50px"
            >
              <div class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                <svg
                  class="h-5 w-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  ></path>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-sm font-medium text-gray-900">Upload Invoice</p>
                <p class="text-xs text-gray-500">
                  Click to upload PDF, JPG, PNG files
                </p>
              </div>
              <input
                ref="fileInput"
                type="file"
                class="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                @change="handleFileUpload"
              />
            </div>

            <div
              v-if="uploadedFileName"
              class="flex items-center gap-2 rounded bg-green-50 p-2 text-sm text-green-600"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              {{ uploadedFileName }}
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <ramro-button
            variant="secondary"
            class="px-6 py-2"
            @click="discardWhatsAppChanges"
          >
            Discard
          </ramro-button>
          <ramro-button
            variant="primary"
            class="px-6 py-2"
            @click="saveWhatsAppChanges"
          >
            Save
          </ramro-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AnalyticsIntegration from "../../apis/AnalyticsIntegration";
import RamroButton from "@/components/Button/Button.vue";
export default {
  name: "AdvanceSettingTab",
  components: {
    RamroButton
  },
  data() {
    return {
      bannerEnabled: false,
      whatsappConfig: null,
      loading: {
        toggle: false,
        cache: false,
        whatsapp: false
      },
      settings: [
        {
          id: 1,
          type: "toggle",
          label: ""
        },
        {
          id: 2,
          type: "action",
          label: "Clear Cache of Tenant",
          variant: "primary",
          actionLabel: "Clear Cache"
        }
        // Additional actions can be added here
      ],
      showWhatsAppSection: true,
      isWhatsAppEnabled: false,
      originalWhatsAppEnabled: false,
      uploadedFile: null,
      uploadedFileName: "",
      whatsappStatus: "disabled",
      whatsappAttachmentUrl: null
    };
  },
  computed: {
    tenantId() {
      return this.$route?.params?.id || null;
    },
    toggleLoading() {
      return this.loading.toggle;
    },
    cacheLoading() {
      return this.loading.cache;
    },
    whatsappStatusLabel() {
      if (!this.whatsappConfig) {
        return this.toggleLoading || this.loading.whatsapp ? "Loading..." : "Unknown";
      }
      const status = this.resolveStatus(this.whatsappConfig);
      if (typeof status === "string") {
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
      if (typeof status === "boolean") {
        return status ? "Enabled" : "Disabled";
      }
      return "Unknown";
    }
  },
  mounted() {
    this.initializeConfigs();
  },
  methods: {
    async initializeConfigs() {
      await Promise.all([
        this.fetchWhatsappConfig(),
        this.fetchGeneralConfig()
      ]);
    },
    resolveStatus(config) {
      if (!config || typeof config !== "object") {
        return undefined;
      }

      const directStatus =
        config.whatsapp_status ??
        config.status ??
        config.banner_status ??
        config.enabled;

      if (directStatus !== undefined) {
        return directStatus;
      }

      const meta = config.meta ?? config.metadata ?? {};
      return meta.payment_banner ?? meta.banner_status ?? meta.whatsapp_status;
    },
    resolveBannerStatus(config) {
      if (!config || typeof config !== "object") {
        return undefined;
      }

      if (config.payment_banner !== undefined) {
        return config.payment_banner;
      }

      if (config.banner_status !== undefined) {
        return config.banner_status;
      }

      const meta = config.meta ?? config.metadata ?? {};
      return meta.payment_banner ?? meta.banner_status;
    },
    buttonClass(variant) {
      const base =
        "rounded-md border px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-none disabled:cursor-not-allowed disabled:opacity-60";
      if (variant === "danger") {
        return `${base} border-red-500 bg-red-500 text-white hover:bg-red-600`;
      }
      return `${base} border-blue-500 bg-blue-500 text-white hover:bg-blue-600`;
    },
    actionLabel(item) {
      if (item.id === 2 && this.cacheLoading) {
        return "Clearing...";
      }
      return item.actionLabel;
    },
    actionDisabled(id) {
      if (!this.tenantId) {
        return true;
      }
      if (id === 2) {
        return this.cacheLoading;
      }
      return false;
    },
    getToaster() {
      return this.$toasted || this.$toast || null;
    },
    showToast(type, message) {
      const toaster = this.getToaster();
      if (!toaster) {
        return;
      }
      const handler = toaster[type];
      if (typeof handler === "function") {
        handler(message);
      }
    },
    async fetchGeneralConfig() {
      if (!this.tenantId) {
        return;
      }

      try {
        const response = await AnalyticsIntegration.getTenantConfig(this.tenantId);
        const payload = response?.data ?? response ?? {};
        const config = payload?.data ?? payload ?? {};
        this.whatsappConfig = {
          ...(this.whatsappConfig || {}),
          ...config
        };

        const bannerStatus = this.resolveBannerStatus(config);
        if (bannerStatus !== undefined) {
          this.bannerEnabled = this.isStatusEnabled(bannerStatus);
        }
      } catch (error) {
        this.showToast("error", "Failed to load tenant config");
      }
    },
    async fetchWhatsappConfig() {

      if (!this.tenantId) {
        return;
      }
      this.loading.whatsapp = true;
      try {
        const response = await AnalyticsIntegration.getTenantWhatsappConfig(this.tenantId);
        const payload = response?.data ?? response ?? {};
        const config = payload?.data ?? payload ?? {};
        this.whatsappConfig = config;
        const whatsappStatus = config.whatsapp_status ?? this.resolveStatus(config);
        const bannerStatus = this.resolveBannerStatus(config);
        const isEnabled = this.isStatusEnabled(whatsappStatus);
        this.isWhatsAppEnabled = isEnabled;
        this.originalWhatsAppEnabled = isEnabled;
        this.whatsappStatus = typeof whatsappStatus === "string" ? whatsappStatus : (isEnabled ? "enabled" : "disabled");
        if (bannerStatus !== undefined) {
          this.bannerEnabled = this.isStatusEnabled(bannerStatus);
        }
        this.whatsappAttachmentUrl = config.attachment_url || null;
        this.whatsappStatus = this.isWhatsAppEnabled ? "enabled" : "disabled";
      } catch (error) {
        this.showToast("error", "Failed to load WhatsApp config");
      } finally {
        this.loading.whatsapp = false;
      }
    },
    triggerFileUpload() {
      if (!this.$refs.fileInput) {
        return;
      }
      this.$refs.fileInput.click();
    },
    handleFileUpload(event) {
      const file = event.target?.files?.[0];
      if (!file) {
        this.uploadedFile = null;
        this.uploadedFileName = "";
        return;
      }
      this.uploadedFile = file;
      this.uploadedFileName = file.name;
    },
    discardWhatsAppChanges() {
      this.isWhatsAppEnabled = this.originalWhatsAppEnabled;
      this.uploadedFile = null;
      this.uploadedFileName = "";
    },
    toggleWhatsApp() {
      if (!this.tenantId) {
        this.isWhatsAppEnabled = this.originalWhatsAppEnabled;
      }
    },
    saveWhatsAppChanges() {
      const status = this.isWhatsAppEnabled ? "enabled" : "disabled";

      if (status === "enabled" && !this.uploadedFile) {
        this.showToast("error", "Attachment is required to enable WhatsApp.");
        return;
      }

      const formData = new FormData();
      formData.append("status", status);
      if (this.uploadedFile) {
        formData.append("attachment", this.uploadedFile);
      }

      this.loading.whatsapp = true;
      axios
        .post(`/tenants/${this.tenantId}/toggle-whatsapp`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then((response) => {
          const message =
            response?.data?.message ||
            response?.message ||
            "WhatsApp status updated successfully";
          this.showToast("success", message);
          this.originalWhatsAppEnabled = this.isWhatsAppEnabled;
          this.whatsappStatus = status;
          this.uploadedFile = null;
          this.uploadedFileName = "";
          this.fetchWhatsappConfig();
        })
        .catch(error => {
          const message =
            error?.response?.data?.message || "Failed to update WhatsApp status";
          this.showToast("error", message);
        })
        .finally(() => {
          this.loading.whatsapp = false;
        });
    },
    async handleToggleBanner(checked) {
      if (!this.tenantId || this.toggleLoading) {
        return;
      }

      const previous = this.bannerEnabled;
      const status = checked ? "enabled" : "disabled";
      this.bannerEnabled = checked;
      this.loading.toggle = true;

      try {
        const response = await AnalyticsIntegration.updateTenantBannerStatus(
          this.tenantId,
          status
        );
        const message = response?.message || `Banner ${status} successfully`;
        this.showToast("success", message);
        if (this.whatsappConfig) {
          this.whatsappConfig = {
            ...this.whatsappConfig,
            status,
            meta: {
              ...(this.whatsappConfig.meta || {}),
              payment_banner: status
            }
          };
        }
      } catch (error) {
        this.bannerEnabled = previous;
        this.showToast("error", "Failed to update banner status");
      } finally {
        this.loading.toggle = false;
      }
    },
    async handleClearCache() {
      if (!this.tenantId || this.cacheLoading) {
        return;
      }

      this.loading.cache = true;
      try {
        await AnalyticsIntegration.clearTenantCache(this.tenantId);
        this.showToast("success", "Cache cleared successfully");
      } catch (error) {
        this.showToast("error", "Failed to clear cache");
      } finally {
        this.loading.cache = false;
      }
    },
    handleAction(id) {
      if (id === 2) {
        this.handleClearCache();
      }
    },
    isStatusEnabled(value) {
      if (typeof value === "string") {
        return value.toLowerCase() === "enabled" || value.toLowerCase() === "active";
      }
      if (typeof value === "boolean") {
        return value;
      }
      return false;
    }
  }
};
</script>

<style scoped>
.config-toggle {
  position: relative;
  width: 42px;
  height: 24px;
  display: inline-block;
}

.config-toggle__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.config-toggle__track {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  width: 42px;
  height: 24px;
  padding: 0 4px;
  border-radius: 9999px;
  background-color: #cbd5e1;
  transition: background-color 0.2s ease;
  box-sizing: border-box;
}

.config-toggle__thumb {
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
  transform: translateX(0);
}

.config-toggle__input:checked+.config-toggle__track {
  background-color: #0398dc;
}

.config-toggle__input:checked+.config-toggle__track .config-toggle__thumb {
  transform: translateX(18px);
}

.config-toggle__input:focus-visible+.config-toggle__track {
  box-shadow: 0 0 0 3px rgba(125, 211, 252, 0.45);
}

.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 48px;
  height: 26px;
  border-radius: 9999px;
  background-color: #d1d5db;
  transition: background-color 0.2s ease;
}

.toggle-switch-on {
  background-color: #22c55e;
}

.toggle-slider {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: transform 0.2s ease;
}

.toggle-switch-on .toggle-slider {
  transform: translateX(22px);
}

.badge-dot {
  top: -16px;
  border-radius: 3px;
  left: 20px;
}
</style>
